import { Object3D } from 'three'
import * as TWEEN from '@tweenjs/tween.js'

import { animateChangeMeshes, TOpacityOption } from '../utils/animate-change-meshes'
import { StoneSize } from '../../../../trillion-common/src/objects/stone-size.object'
import { Configurator } from './Configurator/Configurator'
import { useEffect, useState } from 'react'
import { ANIMATION_DURATION } from '../../viewerSettings'
import { useAppStore } from '../../shared/store/store'

interface SelectCutProps {
  model: Object3D | undefined
  stoneSizes: Array<StoneSize>
}

const getAvailableCuts = (stoneSizes: Array<StoneSize>, choosenCaratSize: number) =>
  stoneSizes
    .map((s, index) => ({ ...s, originalIndex: index }))
    .filter((s) => s.sizeCT === choosenCaratSize)

export const SelectSize = ({ model, stoneSizes }: SelectCutProps) => {
  const setCurrentSize = useAppStore((state) => state.setCurrentSize)
  const uniqueSizes = [...new Set(stoneSizes.map((stone) => stone.sizeCT))].sort((a, b) => a - b)
  const [availableCutsForSize, setAvailableCutsForSize] = useState(
    getAvailableCuts(stoneSizes, uniqueSizes[0]),
  )
  const [currentCutForSize, setCurrentCutForSize] = useState(availableCutsForSize[0])
  const [currentSizeCT, setCurrentSizeCT] = useState(uniqueSizes[0])

  let tweenOutSize: TWEEN.Tween<TOpacityOption>
  let tweenInSize: TWEEN.Tween<TOpacityOption>

  function changeCurrentSizeCT(model: Object3D, currentIndex: number) {
    setCurrentSizeCT(uniqueSizes[currentIndex])
    const newAvailableCuts = getAvailableCuts(stoneSizes, uniqueSizes[currentIndex])
    setAvailableCutsForSize(newAvailableCuts)
  }

  useEffect(() => {
    const { cutType } = currentCutForSize
    const indexOfPrevCutType = availableCutsForSize.findIndex((cut) => cut.cutType === cutType)
    changeCutForSize(model as Object3D, indexOfPrevCutType === -1 ? 0 : indexOfPrevCutType, -1)
  }, [availableCutsForSize])

  function changeCutForSize(model: Object3D, currentIndex: number, prevIndex: number) {
    const newCut = availableCutsForSize[currentIndex]
    const prevCut = currentCutForSize
    setCurrentCutForSize(newCut)
    setCurrentSize(newCut)
    if (tweenInSize) TWEEN.remove(tweenInSize)
    if (tweenOutSize) TWEEN.remove(tweenOutSize)
    for (const group of model.children) {
      const prevMeshes: Array<Object3D> = []
      const currentMeshes: Array<Object3D> = []

      for (const mesh of group.children) {
        stoneSizes.forEach((size, index) => {
          if (size.names.includes(mesh.name) || mesh.name === size.frame) {
            mesh.visible = false
            if (prevIndex > -1 && index === prevCut.originalIndex) {
              prevMeshes.push(mesh)
            } else if (index === newCut.originalIndex) {
              currentMeshes.push(mesh)
            }
          }
        })
      }

      if (prevIndex > -1) {
        tweenOutSize = animateChangeMeshes(prevMeshes, 0, 'out')
      }
      tweenInSize = animateChangeMeshes(
        currentMeshes,
        prevIndex > -1 ? ANIMATION_DURATION : 0,
        'in',
      )
    }
  }

  return (
    <>
      <Configurator
        model={model}
        items={availableCutsForSize}
        currentItem={currentCutForSize}
        title={'Gem cut'}
        onChange={changeCutForSize}
      />
      <Configurator
        model={model}
        items={uniqueSizes}
        currentItem={currentSizeCT}
        title={'Gem size'}
        onChange={changeCurrentSizeCT}
      />
    </>
  )
}
